import { z } from "zod";
import { WindField } from "./wind.ts";
import { PrecipField } from "./precip.ts";
import { AuditList, AuditListQuery } from "./audit.ts";
import { BBox, Id, Slug, Status } from "./common.ts";
import { DepthQuery, DepthResult } from "./depth.ts";
import { DiveLakeList, DiveManifest, DiveTileQuery, MeFeatures } from "./dive.ts";
import {
  ObjectModel,
  ObjectModelList,
  ObjectModelListQuery,
  ObjectModelUpdate,
  ObjectModelUploadQuery,
} from "./object-models.ts";
import { SpotExport, SpotExportQuery } from "./export.ts";
import { Lake, LakeList, LakeUpdate } from "./lakes.ts";
import {
  DiveObject,
  DiveObjectCreate,
  DiveObjectUpdate,
  SightingCreate,
  SightingList,
} from "./objects.ts";
import { SpotCreate, SpotDetail, SpotList, SpotUpdate } from "./spots.ts";
import {
  Suggestion,
  SuggestionApprove,
  SuggestionCreate,
  SuggestionDetail,
  SuggestionList,
  SuggestionListQuery,
  MySuggestionsQuery,
  SuggestionReject,
} from "./suggestions.ts";
import { PublicConfig, SiteSettings, SiteSettingsUpdate } from "./settings.ts";
import { TerrainQuery } from "./terrain.ts";
import {
  AdminUser,
  AdminUserCreate,
  AdminUserList,
  AdminUserListQuery,
  AdminUserUpdate,
  Me,
  MeUpdate,
} from "./users.ts";

/**
 * The full /api surface (besides better-auth's /api/auth/*).
 *
 * Rules both sides rely on:
 * - JSON bodies/responses unless noted; errors are always `ApiError` with the HTTP status
 *   matching `error.code` (400 bad_request, 401 unauthorized, 403 forbidden, 404 not_found,
 *   409 conflict, 429 rate_limited, 500 internal).
 * - `auth: "public"` routes return only `status: "published"` rows to anonymous callers;
 *   a logged-in moderator/admin additionally sees draft/pending rows and may pass `?status=`.
 * - Deleted (soft) rows are invisible everywhere and DELETE returns 204 with no body.
 * - Writes require a same-origin request (Origin header checked) plus a valid session cookie.
 *
 * Decided semantics (Lead, after Adversary round 1 — implement exactly):
 * - Auth order: no/invalid session → 401; wrong role or banned → 403; THEN, for writes,
 *   missing or foreign Origin → 403. (So a logged-out write is always 401.)
 * - `/api/me`: anonymous → 401; any signed-in role (admin, moderator, diver) → 200.
 * - Draft/pending/deleted spot or object requested by an anonymous caller → 404 (never 403).
 * - `?status=` from an anonymous caller is ignored (always published) — but it is still
 *   validated first, so an invalid value is 400 for everyone. A moderator without
 *   `?status=` gets every non-deleted status (draft, published, pending).
 * - `objectCount` and `objects` always describe the same set: what the caller may see.
 * - Slugs: derived with `slugify(name)` (fallback "spot"); a derived slug that is taken gets
 *   "-2", "-3", …; an EXPLICIT slug (create or PATCH) that is taken → 409 conflict.
 * - DELETE spot soft-deletes its objects too. Any route on a deleted row → 404.
 * - createObject with an unknown or deleted `spotId` → 400 bad_request (details path
 *   ["spotId"]). Objects cannot move between spots (no spotId in update): delete + create.
 * - `status: "pending"` from a moderator is accepted (reserved for diver suggestions).
 * - PATCH replaces each given top-level key whole; `{}` → 200 unchanged, no audit row.
 * - Lakes are addressed by slug, spots/objects by UUID id, users by opaque string id.
 *
 * Diver accounts & suggestions (wave 2):
 * - Divers sign up themselves via better-auth (magic link / passkey) at /api/auth/*. Sign-up
 *   MUST yield role "diver"; a sign-up or update-user body carrying `role` is rejected (400);
 *   only PATCH /api/admin/users changes roles. Names are 1–120 chars.
 * - `/api/me` (GET/PATCH/DELETE) is auth "diver": any signed-in user, staff included. A banned
 *   user gets 403 with code forbidden and message "account banned" so clients can say so.
 * - DELETE /api/me deletes the caller's account and sessions (staff: 409 — an admin must
 *   demote first). Their suggestions stay, `submittedBy` becomes {id:"deleted", name:"Gelöschtes
 *   Konto"}; pending ones become withdrawn; their sightings stay anonymously.
 * - createSuggestion is for divers only: staff → 400 (they write directly). Targets and
 *   `payload.spotId` must be published and not deleted, else 404.
 * - A diver sees ONLY their own suggestions (mine, detail of their own id; others → 404).
 *   Moderators see all. Only the owner may withdraw (others → 404).
 * - Only `pending` suggestions can be approved, rejected or withdrawn; otherwise 409. A moderator
 *   cannot review a suggestion they submitted (409). Approving an edit of a deleted target, or
 *   an object_create whose spot is gone → 409; a stale edit → 409 unless `force: true`.
 * - Approval applies the payload exactly like the moderator's own POST/PATCH (lake assignment,
 *   depth computation, slug derivation, audit row with actor = moderator and the suggestion id),
 *   then stores `resultId`. An edit identical to the current target → 400 at submit.
 * - Limits: > MAX_PENDING_SUGGESTIONS pending per diver → 429. Per IP (same limiter as depth/
 *   terrain): createSuggestion 20/min, createSighting 30/min, better-auth sign-up/magic-link
 *   requests 5/min → 429 with Retry-After.
 * - Registration control (settings.ts): with registration closed or the hourly cap reached, a
 *   magic-link request for an unknown email creates no account and sends no mail but answers
 *   exactly like any other request. Banning (PATCH /api/admin/users/:id {banned:true}) ends all
 *   of that user's sessions at once.
 * - Sightings: any signed-in user; target object must be published (else 404); today/past
 *   only, using `zurichDate()`; response is the updated object.
 *
 * Dive mode (beta, dive.ts):
 * - Auth "dive": no/invalid session → 401; banned → 403 "account banned"; signed in but neither
 *   admin nor granted `diveAccess` → 403 forbidden with message "dive access required". The
 *   grant is read from the database on every request (not from a cached session), so a revoke
 *   applies to the next request. GET /api/me/features (auth "diver") reports the same decision.
 * - A lake without a built tile set → 404 on manifest/overview/tiles and absent from the list.
 *   A tile not in `manifest.tiles` → 404. A `?v=` that is not the current version → 409.
 * - Tile/overview bodies are TERRAIN binaries with `waterLevelM: null`; the manifest carries the
 *   live water level. The job stores them gzip-compressed; the API sends them with
 *   `Content-Encoding: gzip` when the request accepts gzip, else decompressed.
 * - Never `Cache-Control: public`; every dive response has `Vary: Cookie`. Manifest and
 *   /api/me/features: `private, no-cache`; the manifest's ETag is a hash of its serialized body
 *   (version AND live water level), 304 on a matching `If-None-Match`. Tiles/overview: see
 *   `DiveTileQuery`. The service worker never caches /api/dive/* or /api/me/* (default "never").
 *   Browser caches may keep tiles after a revoke; that is accepted (see dive.ts).
 * - PATCH /api/lakes/:slug with a `waterLevelM` at or below the current dive build's `minElevM`
 *   → 409 conflict (the tiles would read as dry land); rebuild the tile set first. A lake whose
 *   build is inconsistent with the live level anyway is left out of /api/dive/lakes, and its
 *   manifest is 500 internal (never a clamped depth).
 * - Read-only GETs; no Origin check. Rate limit AFTER auth, keyed by user id: 600 requests/min
 *   per user across /api/dive/* (tile streaming is bursty) → 429 with Retry-After.
 */
/**
 * `diver` = any signed-in, non-banned user (divers AND staff).
 * `dive` = signed-in, non-banned, and (role admin OR `diveAccess` granted).
 */
export type Auth = "public" | "diver" | "moderator" | "admin" | "dive";

export interface RouteDef {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  auth: Auth;
  query?: z.ZodType;
  body?: z.ZodType;
  /** Absent = 204 No Content. `"binary"` = TERRAIN_CONTENT_TYPE body. */
  response?: z.ZodType | "binary";
  /** `"binary"` = a raw request body (see the route's comment for its content type). */
  bodyKind?: "binary";
  status: 200 | 201 | 204;
}

export const SpotListQuery = z
  .object({
    bbox: z
      .string()
      .transform((s) => s.split(",").map((p) => (p.trim() === "" ? Number.NaN : Number(p))))
      .pipe(BBox)
      .optional(),
    lake: Slug.optional(),
    status: Status.optional(),
  })
  .strict();
export type SpotListQuery = z.infer<typeof SpotListQuery>;
/** What a caller passes (bbox as "w,s,e,n" string). */
export type SpotListQueryInput = z.input<typeof SpotListQuery>;

/** GET /api/objects — moderator list for the admin UI. */
export const ObjectListQuery = z
  .object({ spotId: Id.optional(), status: Status.optional() })
  .strict();
export type ObjectListQuery = z.infer<typeof ObjectListQuery>;
export const ObjectList = z.object({ objects: z.array(DiveObject) }).strict();
export type ObjectList = z.infer<typeof ObjectList>;

export const routes = {
  publicConfig: {
    method: "GET",
    path: "/api/config",
    auth: "public",
    response: PublicConfig,
    status: 200,
  },
  getSettings: {
    method: "GET",
    path: "/api/admin/settings",
    auth: "admin",
    response: SiteSettings,
    status: 200,
  },
  updateSettings: {
    method: "PATCH",
    path: "/api/admin/settings",
    auth: "admin",
    body: SiteSettingsUpdate,
    response: SiteSettings,
    status: 200,
  },
  health: {
    method: "GET",
    path: "/api/health",
    auth: "public",
    response: z.object({ ok: z.literal(true) }),
    status: 200,
  },
  me: { method: "GET", path: "/api/me", auth: "diver", response: Me, status: 200 },
  updateMe: {
    method: "PATCH",
    path: "/api/me",
    auth: "diver",
    body: MeUpdate,
    response: Me,
    status: 200,
  },
  deleteMe: { method: "DELETE", path: "/api/me", auth: "diver", status: 204 },
  meFeatures: {
    method: "GET",
    path: "/api/me/features",
    auth: "diver",
    response: MeFeatures,
    status: 200,
  },

  listLakes: { method: "GET", path: "/api/lakes", auth: "public", response: LakeList, status: 200 },
  /**
   * Public whole-lake overview for the mini-maps (3D spot view and dive mode): a TERRAIN binary
   * (≤ DIVE_OVERVIEW_MAX_SIDE per side, built by the dive:tiles job, also in an overview-only
   * mode for lakes without a dive build) with `meta.waterLevelM` = the LIVE lake level filled in
   * at request time. 404 when no overview was built for the lake. Coarse swisstopo data, so
   * public: `Cache-Control: public, max-age=3600` + ETag (hash of the body) + 304; never
   * precached by the service worker (classify "never").
   */
  lakeOverview: {
    method: "GET",
    path: "/api/lakes/:slug/overview",
    auth: "public",
    response: "binary",
    status: 200,
  },
  /**
   * Public wind forecast for the overview map overlay (see wind.ts): the cached MeteoSwiss
   * ICON-CH grid, `times` trimmed to start at the current hour. 200: `Cache-Control: public,
   * max-age=600` + ETag + 304. 503 (code `internal`, `Retry-After`, `no-store`) while no field
   * has been fetched yet; 404 when the server has wind switched off (`WIND_ENABLED=0`). Never
   * cached by the service worker (classify "never").
   */
  wind: {
    method: "GET",
    path: "/api/weather/wind",
    auth: "public",
    response: WindField,
    status: 200,
  },
  /**
   * Public precipitation forecast for the overview map overlay (see precip.ts): filled by the same
   * refresh as `wind` (no extra upstream calls), `times` trimmed like wind (the first sum ends
   * after the current hour starts). Same caching and status semantics as `wind`: 200 with
   * `public, max-age=600` + ETag + 304; 503 (`internal`, `Retry-After`, `no-store`) before the
   * first field; 404 when `WIND_ENABLED=0` (one switch for the weather overlay). Service worker:
   * "never".
   */
  precip: {
    method: "GET",
    path: "/api/weather/precip",
    auth: "public",
    response: PrecipField,
    status: 200,
  },
  updateLake: {
    method: "PATCH",
    path: "/api/lakes/:slug",
    auth: "admin",
    body: LakeUpdate,
    response: Lake,
    status: 200,
  },

  listSpots: {
    method: "GET",
    path: "/api/spots",
    auth: "public",
    query: SpotListQuery,
    response: SpotList,
    status: 200,
  },
  /**
   * Public, versioned export for other tools (see export.ts). CORS `*`, GET only; cacheable
   * (`Cache-Control: public, max-age=300` + ETag; 304 on `If-None-Match`).
   */
  exportSpots: {
    method: "GET",
    path: "/api/export/v1/spots",
    auth: "public",
    query: SpotExportQuery,
    response: SpotExport,
    status: 200,
  },
  getSpot: {
    method: "GET",
    path: "/api/spots/:slug",
    auth: "public",
    response: SpotDetail,
    status: 200,
  },
  createSpot: {
    method: "POST",
    path: "/api/spots",
    auth: "moderator",
    body: SpotCreate,
    response: SpotDetail,
    status: 201,
  },
  updateSpot: {
    method: "PATCH",
    path: "/api/spots/:id",
    auth: "moderator",
    body: SpotUpdate,
    response: SpotDetail,
    status: 200,
  },
  deleteSpot: { method: "DELETE", path: "/api/spots/:id", auth: "moderator", status: 204 },

  listObjects: {
    method: "GET",
    path: "/api/objects",
    auth: "moderator",
    query: ObjectListQuery,
    response: ObjectList,
    status: 200,
  },
  createObject: {
    method: "POST",
    path: "/api/objects",
    auth: "moderator",
    body: DiveObjectCreate,
    response: DiveObject,
    status: 201,
  },
  updateObject: {
    method: "PATCH",
    path: "/api/objects/:id",
    auth: "moderator",
    body: DiveObjectUpdate,
    response: DiveObject,
    status: 200,
  },
  deleteObject: { method: "DELETE", path: "/api/objects/:id", auth: "moderator", status: 204 },

  /** Public (visibility follows the object). See object-models.ts. */
  listObjectModels: {
    method: "GET",
    path: "/api/object-models",
    auth: "public",
    query: ObjectModelListQuery,
    response: ObjectModelList,
    status: 200,
  },
  /**
   * The `.glb` file (MODEL_CONTENT_TYPE). `public, max-age=86400` + `ETag: "<sha256>"` (304) for
   * published objects' files, `private, no-cache` when only staff may see it; never immutable.
   * 404 when no visible object uses it. Per-IP 60/min. Params: ObjectModelFileParam. See
   * object-models.ts for serving (disk cache or stream, never whole-file buffering).
   */
  getObjectModelFile: {
    method: "GET",
    path: "/api/object-models/:file",
    auth: "public",
    response: "binary",
    status: 200,
  },
  /**
   * Raw `.glb` body (MODEL_CONTENT_TYPE). Auth + Origin before reading the body; > MODEL_MAX_BYTES
   * → 413 `payload_too_large` (Content-Length or streamed count); validation per MODEL_LIMITS →
   * 400; total store over MODEL_TOTAL_MAX_BYTES → 409; 10/min per admin. Metadata in the query.
   */
  putObjectModel: {
    method: "PUT",
    path: "/api/objects/:id/model",
    auth: "admin",
    query: ObjectModelUploadQuery,
    bodyKind: "binary",
    response: ObjectModel,
    status: 200,
  },
  updateObjectModel: {
    method: "PATCH",
    path: "/api/objects/:id/model",
    auth: "admin",
    body: ObjectModelUpdate,
    response: ObjectModel,
    status: 200,
  },
  deleteObjectModel: {
    method: "DELETE",
    path: "/api/objects/:id/model",
    auth: "admin",
    status: 204,
  },

  listSightings: {
    method: "GET",
    path: "/api/objects/:id/sightings",
    auth: "public",
    response: SightingList,
    status: 200,
  },
  createSighting: {
    method: "POST",
    path: "/api/objects/:id/sightings",
    auth: "diver",
    body: SightingCreate,
    response: DiveObject,
    status: 201,
  },

  createSuggestion: {
    method: "POST",
    path: "/api/suggestions",
    auth: "diver",
    body: SuggestionCreate,
    response: Suggestion,
    status: 201,
  },
  listMySuggestions: {
    method: "GET",
    path: "/api/suggestions/mine",
    auth: "diver",
    query: MySuggestionsQuery,
    response: SuggestionList,
    status: 200,
  },
  listSuggestions: {
    method: "GET",
    path: "/api/suggestions",
    auth: "moderator",
    query: SuggestionListQuery,
    response: SuggestionList,
    status: 200,
  },
  getSuggestion: {
    method: "GET",
    path: "/api/suggestions/:id",
    auth: "diver",
    response: SuggestionDetail,
    status: 200,
  },
  approveSuggestion: {
    method: "POST",
    path: "/api/suggestions/:id/approve",
    auth: "moderator",
    body: SuggestionApprove,
    response: Suggestion,
    status: 200,
  },
  rejectSuggestion: {
    method: "POST",
    path: "/api/suggestions/:id/reject",
    auth: "moderator",
    body: SuggestionReject,
    response: Suggestion,
    status: 200,
  },
  withdrawSuggestion: {
    method: "DELETE",
    path: "/api/suggestions/:id",
    auth: "diver",
    status: 204,
  },

  depth: {
    method: "GET",
    path: "/api/depth",
    auth: "public",
    query: DepthQuery,
    response: DepthResult,
    status: 200,
  },
  terrain: {
    method: "GET",
    path: "/api/terrain",
    auth: "public",
    query: TerrainQuery,
    response: "binary",
    status: 200,
  },

  listDiveLakes: {
    method: "GET",
    path: "/api/dive/lakes",
    auth: "dive",
    response: DiveLakeList,
    status: 200,
  },
  diveManifest: {
    method: "GET",
    path: "/api/dive/:slug/manifest",
    auth: "dive",
    response: DiveManifest,
    status: 200,
  },
  /** Whole-lake low-resolution terrain (≤ DIVE_OVERVIEW_MAX_SIDE per side) for the mini-map. */
  diveOverview: {
    method: "GET",
    path: "/api/dive/:slug/overview",
    auth: "dive",
    query: DiveTileQuery,
    response: "binary",
    status: 200,
  },
  /** One DIVE_TILE_SIDE² terrain tile; params parsed with `DiveTileParams`. */
  diveTile: {
    method: "GET",
    path: "/api/dive/:slug/tiles/:x/:y",
    auth: "dive",
    query: DiveTileQuery,
    response: "binary",
    status: 200,
  },

  listUsers: {
    method: "GET",
    path: "/api/admin/users",
    auth: "admin",
    query: AdminUserListQuery,
    response: AdminUserList,
    status: 200,
  },
  createUser: {
    method: "POST",
    path: "/api/admin/users",
    auth: "admin",
    body: AdminUserCreate,
    response: AdminUser,
    status: 201,
  },
  updateUser: {
    method: "PATCH",
    path: "/api/admin/users/:id",
    auth: "admin",
    body: AdminUserUpdate,
    response: AdminUser,
    status: 200,
  },
  /** Admin action log (see audit.ts): private, no-store; never cached by the service worker. */
  listAudit: {
    method: "GET",
    path: "/api/admin/audit",
    auth: "admin",
    query: AuditListQuery,
    response: AuditList,
    status: 200,
  },
} as const satisfies Record<string, RouteDef>;

export type RouteName = keyof typeof routes;
